import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BlockDesktop as Block } from '@alfa-bank/site-block/desktop';
import { TextDesktop as Text } from '@alfa-bank/site-text-v2/desktop';
import { AnalyticsEventSender } from '@alfa-bank/site-analytics-event-sender/analytics-event-sender';
import { TagDesktop as Tag } from '@alfa-bank/site-tag/desktop';
import cn from 'classnames';

import styles from './styles.css';
import { goToSearch } from './helpers';
import type { TSuggestBlockDesktopProps } from './models';

const suggestBlockStateMap = {
    none: '',
    open: styles.isOpen,
    close: styles.isClosed,
};

export const SuggestBlockDesktop = ({
    closeOverlay,
    isFocused,
    searchInputRef,
    suggestList,
    suggestTitle,
}: TSuggestBlockDesktopProps) => {
    const [isTouched, setisTouched] = useState<keyof typeof suggestBlockStateMap>('none');
    const defaultOverflow = useRef<string>();

    const getStyleForBlock = useCallback(() => {
        return suggestBlockStateMap[isTouched];
    }, [isTouched]);

    useEffect(() => {
        /** clickOutside
         *  При клике вне родителя и состоянии SuggestBlock = open
         *  происходит закрытие SuggestBlock
         *  убирается блок скролла
         *  и вызывается коллбек на закрытие overlay
         */
        const clickOutside = (e: MouseEvent) => {
            if (!searchInputRef?.current?.contains(e.target as Node) && isTouched === 'open') {
                setisTouched('close');
                if (defaultOverflow.current !== undefined) {
                    document.body.style.overflow = defaultOverflow.current;
                }
                closeOverlay();
            }
        };
        document.addEventListener('click', clickOutside);

        return () => document.removeEventListener('click', clickOutside);
    }, [closeOverlay, isTouched, searchInputRef]);

    useEffect(() => {
        let suggestBlockTimeOut: NodeJS.Timeout;
        if (isFocused && isTouched !== 'open') {
            searchInputRef?.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
            suggestBlockTimeOut = setTimeout(() => {
                setisTouched('open');
                defaultOverflow.current = document.body.style.overflow;
                document.body.style.overflow = 'hidden';
            }, 300);
        }

        return () => {
            clearTimeout(suggestBlockTimeOut);
        };
    }, [isFocused, isTouched, searchInputRef]);

    return (
        <div className={styles.container}>
            <Block
                className={cn(styles.suggestBlock, styles.external, getStyleForBlock())}
                dataTestId={`suggest-block-${isTouched}`}
                styler={{ borderRadius: 24, padding: 24, paddingBottom: 12 }}
                theme="grey"
            >
                <Text styler={{ marginTop: 0, marginBottom: 16 }} view="small">
                    {suggestTitle}
                </Text>
                {suggestList?.map(({ analytics, link, title }) => (
                    <AnalyticsEventSender
                        key={title}
                        $events={{
                            onClick: analytics,
                        }}
                    >
                        <Tag
                            classNames={cn(styles.external, styles.tag)}
                            dataTestId={title}
                            label={title}
                            onClick={() => goToSearch(link)}
                            role="link"
                            size="xs"
                            styler={{ marginTop: 8 }}
                            value={title}
                        />
                    </AnalyticsEventSender>
                ))}
            </Block>
        </div>
    );
};
